import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

function t(field, lang = 'en') {
  if (!field) return '';
  return field[lang] || field.en || '';
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('en');

  // Force-change state (when admin resets password)
  const [forceChange, setForceChange] = useState(false);
  const [fcNewPassword, setFcNewPassword] = useState('');
  const [fcConfirmPassword, setFcConfirmPassword] = useState('');
  const [fcError, setFcError] = useState('');
  const [fcLoading, setFcLoading] = useState(false);

  useEffect(() => {
    const savedLang = localStorage.getItem('ets_lang');
    if (savedLang) setLang(savedLang);
  }, []);

  const content = {
    desc: { en: 'Administrative access portal. Manage faculty rooms, student hub, and financial auditing with precision.', zh: '行政管理入口網站。精準管理導師室、學生中心和財務審計。' },
    title: { en: 'Admin Login', zh: '管理員登入' },
    subtitle: { en: 'Welcome back. Please enter your credentials.', zh: '歡迎回來。請輸入您的登入憑證。' },
    emailLabel: { en: 'Email Address', zh: '電子郵件地址' },
    passwordLabel: { en: 'Password', zh: '密碼' },
    signIn: { en: 'Sign In to Dashboard', zh: '登入控制台' },
    authenticating: { en: 'Authenticating...', zh: '驗證中...' },
    forgotPassword: { en: 'Forgot your password?', zh: '忘記密碼？' },
    back: { en: '← Back to Public Website', zh: '← 返回公開網站' },
    errEmpty: { en: 'Please enter both email and password.', zh: '請輸入電子郵件和密碼。' },
    errInvalid: { en: 'Invalid email or password.', zh: '電子郵件或密碼無效。' }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError(t(content.errEmpty, lang));
    
    setLoading(true);
    setError('');
    
    try {
      const res = await authAPI.login(email, password);
      const { token, user } = res.data;

      // Block non-admin users (students, parents, tutors) from the web admin portal
      if (user.role !== 'admin') {
        setError('Access Restricted: This web portal is for Academy Administrators only. Students, parents, and tutors must log in using the ETS Mobile App on their phone.');
        return;
      }

      // If the admin reset the user's password, block access until they change it
      if (user.mustChangePassword) {
        // Store temporarily so the change-password call has a valid auth token
        localStorage.setItem('ets_token', token);
        setForceChange(true);
        return;
      }

      login(token, user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || t(content.errInvalid, lang));
    } finally {
      setLoading(false);
    }
  };

  const handleForceChangePassword = async (e) => {
    e.preventDefault();
    setFcError('');
    if (!fcNewPassword || fcNewPassword.length < 6) {
      return setFcError('New password must be at least 6 characters.');
    }
    if (fcNewPassword !== fcConfirmPassword) {
      return setFcError('Passwords do not match.');
    }
    setFcLoading(true);
    try {
      // Use the temp token stored above; backend will hash + clear mustChangePassword
      const res = await authAPI.changePassword({ currentPassword: password, newPassword: fcNewPassword });
      // Backend returns a fresh token with mustChangePassword: false
      const { token } = res.data;
      const meRes = await authAPI.getMe();
      login(token, meRes.data);
      navigate('/dashboard');
    } catch (err) {
      setFcError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setFcLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* ── FORCE CHANGE PASSWORD SCREEN ── */}
      {forceChange && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(17,17,17,.97)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <form onSubmit={handleForceChangePassword} style={{
            background: '#fff', borderRadius: 20, padding: '40px 36px',
            maxWidth: 440, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.5)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
                Change Your Password
              </h2>
              <p style={{ fontSize: 13, color: 'var(--mid-grey)', lineHeight: 1.6 }}>
                Your password was reset by an administrator. You must create a new personal password before accessing the system.
              </p>
            </div>

            {fcError && (
              <div style={{ background: 'rgba(192,57,43,.05)', color: 'var(--red)', padding: '10px 14px', fontSize: 13, borderLeft: '3px solid var(--red)', marginBottom: 20, borderRadius: 4 }}>
                {fcError}
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label-style">New Password</label>
              <input
                className="form-input-style"
                type="password"
                placeholder="Minimum 6 characters"
                value={fcNewPassword}
                onChange={e => setFcNewPassword(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="form-group" style={{ marginBottom: 28 }}>
              <label className="form-label-style">Confirm New Password</label>
              <input
                className="form-input-style"
                type="password"
                placeholder="Re-enter your new password"
                value={fcConfirmPassword}
                onChange={e => setFcConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', padding: '14px', justifyContent: 'center', background: 'var(--wine)' }}
              disabled={fcLoading}
            >
              {fcLoading
                ? <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', marginRight: 8, verticalAlign: 'middle' }} />Setting Password…</>
                : '✓ Set New Password & Continue'
              }
            </button>
          </form>
        </div>
      )}

      {/* Visual Panel */}
      <div className="login-visual" style={{ 
        background: 'linear-gradient(rgba(123, 29, 58, 0.75), rgba(17, 17, 17, 0.85)), url("/assets/login-bg.jpeg") center/cover no-repeat',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px',
        color: '#fff',
        position: 'relative'
      }}>
        {/* Language Toggle in top left of visual panel */}
        <div style={{ position: 'absolute', top: 40, left: 60, display: 'flex', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 20, padding: 4, backdropFilter: 'blur(5px)' }}>
          <button 
            onClick={() => { setLang('en'); localStorage.setItem('ets_lang', 'en'); }}
            style={{ background: lang === 'en' ? '#c04060' : 'transparent', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >EN</button>
          <button 
            onClick={() => { setLang('zh'); localStorage.setItem('ets_lang', 'zh'); }}
            style={{ background: lang === 'zh' ? '#c04060' : 'transparent', color: '#fff', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >繁中</button>
        </div>

        <div style={{ marginBottom: 40, marginTop: 40 }}>
          <img src="/assets/logo.png" alt="ETS" style={{ width: 60, height: 60, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', marginBottom: 20 }} />
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>Every Talent <br/>Shines <span style={{ color: '#c04060' }}>Academy</span></h1>
        </div>
        <p style={{ fontSize: 18, opacity: 0.7, maxWidth: 400, lineHeight: 1.6 }}>
          {t(content.desc, lang)}
        </p>
      </div>

      {/* Form Panel */}
      <div className="login-form-side" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {/* Language Toggle for mobile view (when visual panel is hidden) */}
        <div className="mobile-lang-toggle" style={{ position: 'absolute', top: 20, right: 20, display: 'flex', backgroundColor: '#f0f0f0', borderRadius: 20, padding: 4 }}>
          <button 
            onClick={() => { setLang('en'); localStorage.setItem('ets_lang', 'en'); }}
            style={{ background: lang === 'en' ? '#c04060' : 'transparent', color: lang === 'en' ? '#fff' : '#666', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >EN</button>
          <button 
            onClick={() => { setLang('zh'); localStorage.setItem('ets_lang', 'zh'); }}
            style={{ background: lang === 'zh' ? '#c04060' : 'transparent', color: lang === 'zh' ? '#fff' : '#666', border: 'none', borderRadius: 16, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >繁中</button>
        </div>
        <style>{`
          .mobile-lang-toggle { display: none !important; }
          @media (max-width: 900px) {
            .mobile-lang-toggle { display: flex !important; }
          }
        `}</style>

        <form onSubmit={handleLogin} style={{ maxWidth: 400, width: '100%', padding: '40px' }}>
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: '#111', marginBottom: 8 }}>{t(content.title, lang)}</h2>
            <p style={{ color: '#9a9a9a', fontSize: 14 }}>{t(content.subtitle, lang)}</p>
          </div>

          {error && (
            <div style={{ 
              background: 'rgba(192, 57, 43, 0.05)', 
              color: '#c0392b', 
              padding: '12px 16px', 
              fontSize: 13, 
              borderLeft: '3px solid #c0392b',
              marginBottom: 24 
            }}>
              {error}
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label-style">{t(content.emailLabel, lang)}</label>
            <input 
              className="form-input-style" 
              type="email" 
              placeholder="admin@ets.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: 32 }}>
            <label className="form-label-style">{t(content.passwordLabel, lang)}</label>
            <input 
              className="form-input-style" 
              type="password" 
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', padding: '14px', justifyContent: 'center' }}
            disabled={loading}
          >
            {loading ? t(content.authenticating, lang) : t(content.signIn, lang)}
          </button>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <a href="/forgot-password" style={{ color: '#7b1d3a', fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>
              {t(content.forgotPassword, lang)}
            </a>
          </div>

          <div style={{ marginTop: 20, padding: '12px 14px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #eee', textAlign: 'center' }}>
            <span style={{ fontSize: 12, color: '#666', lineHeight: 1.4, display: 'block' }}>
              📱 <strong>Student, Parent, or Tutor?</strong><br />
              Please sign in using the <strong>ETS Mobile App</strong> on iOS or Android.
            </span>
          </div>

          <div style={{ marginTop: 20, textAlign: 'center', paddingTop: 20, borderTop: '1px solid #eee' }}>
            <a href="/" style={{ color: '#9a9a9a', fontSize: 13, textDecoration: 'none' }}>{t(content.back, lang)}</a>
          </div>
        </form>
      </div>
    </div>
  );
}
